import { useClientPortalAPI } from "@certn/api";
import { Modal } from "@certn/cascadia";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  Search,
  SelectItem,
  Select,
} from "@certn/cascadia-react";
import { slugify } from "@certn/utils";
import { css } from "@emotion/css";
import { useFlags } from "launchdarkly-react-client-sdk";
import { useState } from "react";
import { useTranslation } from "react-i18next";

import { PackageList } from "./PackageList";
import { useGroupSelection } from "./useGroupSelection";

interface Props {
  setIsOpen: (isOpen: boolean) => void;
}

export function SelectPackageModal({ setIsOpen }: Props) {
  const { t } = useTranslation();
  const { packageImprovements } = useFlags();
  const [search, setSearch] = useState("");

  const { data: groups } = useClientPortalAPI(
    "/api/internal/client-portal/customer-accounts/customer-groups/"
  );

  const { selectedGroup, handleGroupChange } = useGroupSelection(groups);

  return (
    <Modal isOpen testID="select-package-modal">
      <Dialog width="860px">
        <DialogHeader hideDivider onClose={() => setIsOpen(false)}>
          {t("Select package")}
        </DialogHeader>
        <DialogContent padding="space-0">
          <div className={styles.controls}>
            <div className={css({ width: 242 })}>
              <Select
                items={groups}
                testID="package-selection-group-select"
                selectedKey={selectedGroup}
                isRequired
                name="group"
                aria-label={t("Group")}
                label={t("Group")}
                onSelectionChange={(key) =>
                  handleGroupChange(key?.toString() ?? "")
                }
              >
                {(item) => (
                  <SelectItem
                    id={item.id}
                    textValue={item.name}
                    key={item.id}
                    data-testid={slugify(item.name)}
                  >
                    {item.name}
                  </SelectItem>
                )}
              </Select>
            </div>
            {packageImprovements && (
              <div className={styles.search}>
                <Search
                  label={t("Search packages")}
                  placeholder={t("Search by package name")}
                  value={search}
                  onChange={setSearch}
                  testID="select-package-search"
                />
              </div>
            )}
          </div>
          <PackageList
            selectedGroup={selectedGroup}
            search={packageImprovements ? search : undefined}
          />
        </DialogContent>
      </Dialog>
    </Modal>
  );
}

const styles = {
  controls: css({
    padding: "var(--space-4)",
    display: "flex",
    gap: "var(--space-4)",
    flexWrap: "wrap",
    alignItems: "flex-end",
  }),
  search: css({
    flex: 1,
    minWidth: "calc(var(--space-46) + var(--space-12))",
  }),
};
